/**
 * Platform-page SVG assets (sapphire-dominant, gold accents only at connection nodes).
 * Each asset is self-contained, premium, and uses subtle animation.
 */
import { Mail, Settings, Users, Building2, BarChart3, Cloud, FileText, Briefcase, ShieldCheck, Lock } from "lucide-react";
import { IMAGES } from "@/lib/site";

const GOLD = "#F59E0B";
const GOLD_BRIGHT = "#FBBF24";
const SAPPHIRE = "#60A5FA";
const SAPPHIRE_CORE = "#9BC7FF";

/* ────────────────────────────────────────────────────────── */
/* 01 — HERO INTELLIGENCE HUB                                 */
/* ────────────────────────────────────────────────────────── */
export function HeroIntelligenceHub() {
  // Clean radial label layout — 7 evenly spaced positions around the hub.
  // `lines` controls multi-line stacking for wider labels so they don't bleed
  // into adjacent columns.
  const labels = [
    { t: "Data Intelligence",                                  angle: 270 },
    { t: "Analytics & Insights",  lines: ["Analytics",   "& Insights"],  angle: 320 },
    { t: "Customer Engagement",   lines: ["Customer",    "Engagement"],  angle:  10 },
    { t: "Lead Intelligence",                                  angle:  60 },
    { t: "CRM Intelligence",                                   angle: 110 },
    { t: "Workflows",                                          angle: 160 },
    { t: "AI Automation",                                      angle: 210 },
  ];
  const R = 36; // % radius from center — labels closer to the hub
  return (
    <div className="relative w-full aspect-square max-w-[560px] mx-auto" aria-hidden>
      {/* Ambient sapphire bloom that bleeds into hero */}
      <div
        className="absolute inset-[-15%] rounded-full pointer-events-none"
        style={{
          background: "radial-gradient(closest-side, rgba(37,99,235,0.40), transparent 70%)",
          filter: "blur(40px)",
        }}
      />
      {/* Visual graphic ONLY — scaled & cropped to hide image's baked-in labels */}
      <div className="absolute inset-[12%] overflow-hidden rounded-full">
        <img
          src={IMAGES.platformHeroHub}
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover object-center slow-float breathe-glow"
          style={{ transform: "scale(1.85)" }}
          draggable="false"
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "radial-gradient(closest-side, transparent 55%, rgba(10,15,26,0.85) 100%)",
          }}
        />
      </div>

      {/* Gold intelligence network overlay — nodes + connecting arcs around the hub */}
      <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden>
        <defs>
          <radialGradient id="gold-node" cx="50%" cy="50%" r="50%">
            <stop offset="0%"  stopColor="#FBBF24" stopOpacity="1" />
            <stop offset="55%" stopColor="#F59E0B" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#F59E0B" stopOpacity="0" />
          </radialGradient>
        </defs>
        {[0,1,2,3,4,5,6,7,8,9,10,11].map(i => {
          const a = (i / 12) * Math.PI * 2 - Math.PI / 2;
          const x = 50 + 32 * Math.cos(a);
          const y = 50 + 32 * Math.sin(a);
          return (
            <g key={i}>
              <circle cx={x} cy={y} r="2.4" fill="url(#gold-node)" opacity="0.9" />
              <circle cx={x} cy={y} r="0.7" fill="#FBBF24" className="pulse-glow" style={{ animationDelay: `${(i % 5) * 0.35}s` }} />
            </g>
          );
        })}
        {/* Dotted connection arcs */}
        <circle cx="50" cy="50" r="32" fill="none" stroke="#F59E0B" strokeWidth="0.18" opacity="0.55" strokeDasharray="0.8 1.5" />
        <circle cx="50" cy="50" r="32" fill="none" stroke="#FBBF24" strokeWidth="0.06" opacity="0.85" />
      </svg>

      {/* HTML radial labels */}
      {labels.map((l, i) => {
        const rad = (l.angle * Math.PI) / 180;
        const x = 50 + R * Math.cos(rad);
        const y = 50 + R * Math.sin(rad);
        const anchorRight = x < 50;
        const isMultiline = Array.isArray(l.lines);
        return (
          <div
            key={i}
            className={`absolute z-10 px-2.5 py-1.5 rounded-md text-[10.5px] sm:text-[12px] font-medium tracking-tight text-platinum ${isMultiline ? "leading-[1.15] text-center" : "whitespace-nowrap"}`}
            style={{
              left: `${x}%`,
              top: `${y}%`,
              transform: `translate(${anchorRight ? '-100%' : '0'}, -50%)`,
              background: "rgba(13,18,32,0.7)",
              border: "1px solid rgba(96,165,250,0.30)",
              backdropFilter: "blur(8px)",
              boxShadow: "0 0 18px -6px rgba(96,165,250,0.45)",
            }}
          >
            {isMultiline ? (
              l.lines.map((line, li) => <div key={li}>{line}</div>)
            ) : (
              l.t
            )}
          </div>
        );
      })}
    </div>
  );
}


/* ────────────────────────────────────────────────────────── */
/* 02 — UNIFIED DATA FOUNDATION DIAGRAM                       */
/* ────────────────────────────────────────────────────────── */
export function DataFoundationDiagram() {
  return (
    <div className="relative w-full" aria-hidden>
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(closest-side, rgba(37,99,235,0.22), transparent 70%)",
          filter: "blur(24px)",
        }}
      />
      <div className="relative slow-float">
        <img
          src={IMAGES.platformDataFoundation}
          alt="Unified Data Foundation — RealityOS Core"
          className="relative w-full h-auto object-contain breathe-glow"
          draggable="false"
        />
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────── */
/* 03 — AUTONOMOUS INTELLIGENCE NETWORK                       */
/* ────────────────────────────────────────────────────────── */
export function AutonomousIntelligenceMesh() {
  return (
    <div className="relative w-full" aria-hidden>
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(closest-side, rgba(96,165,250,0.30), transparent 65%)",
          filter: "blur(28px)",
        }}
      />
      <div className="relative slow-float" style={{ animationDelay: "1.5s" }}>
        <img
          src={IMAGES.platformAutonomous}
          alt="Autonomous Intelligence Layer"
          className="relative w-full h-auto object-contain breathe-glow"
          draggable="false"
        />
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────── */
/* 04 — ENTERPRISE SECURITY SHIELD                            */
/* ────────────────────────────────────────────────────────── */
export function SecurityShield() {
  return (
    <div className="relative w-full aspect-square max-w-[300px] mx-auto" aria-hidden>
      <svg viewBox="0 0 100 100" className="w-full h-full">
        <defs>
          <linearGradient id="shield-body" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={SAPPHIRE_CORE} stopOpacity="0.30" />
            <stop offset="60%" stopColor="#1E40AF" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#0D1B3D" stopOpacity="0.85" />
          </linearGradient>
          <radialGradient id="shield-glow" cx="50%" cy="35%" r="60%">
            <stop offset="0%" stopColor={SAPPHIRE_CORE} stopOpacity="0.65" />
            <stop offset="100%" stopColor="#2563EB" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Halo */}
        <circle cx="50" cy="42" r="32" fill="url(#shield-glow)" opacity="0.65" />

        {/* Shield body */}
        <path
          d="M50 12 L78 22 L78 50 C78 66 65 76 50 80 C35 76 22 66 22 50 L22 22 Z"
          fill="url(#shield-body)"
          stroke={SAPPHIRE_CORE}
          strokeWidth="0.65"
        />
        {/* Inner shield highlight */}
        <path
          d="M50 18 L72 26 L72 50 C72 62 62 70 50 73 C38 70 28 62 28 50 L28 26 Z"
          fill="none"
          stroke={SAPPHIRE}
          strokeWidth="0.4"
          opacity="0.7"
        />

        {/* Lock */}
        <rect x="42" y="44" width="16" height="14" rx="1.6" fill={SAPPHIRE} opacity="0.95" />
        <path d="M44 44 V40 a6 6 0 0 1 12 0 V44" fill="none" stroke={SAPPHIRE_CORE} strokeWidth="1.4" />
        <circle cx="50" cy="50.5" r="1.6" fill={SAPPHIRE_CORE} />
        <line x1="50" y1="50.5" x2="50" y2="55" stroke={SAPPHIRE_CORE} strokeWidth="1.1" />

        {/* Platform rings with gold accents */}
        <ellipse cx="50" cy="86" rx="22" ry="2.2" fill="none" stroke={SAPPHIRE} strokeWidth="0.25" opacity="0.6" />
        <ellipse cx="50" cy="88" rx="16" ry="1.5" fill="none" stroke={SAPPHIRE_CORE} strokeWidth="0.3" opacity="0.7" />
        <ellipse cx="50" cy="86" rx="22" ry="2.2" fill="none" stroke={GOLD} strokeWidth="0.18" opacity="0.55" strokeDasharray="2 1.5" />

        {/* Gold accent dots around base */}
        {[28, 50, 72].map((x, i) => (
          <g key={i}>
            <circle cx={x} cy="86" r="0.9" fill={GOLD_BRIGHT} />
            <circle cx={x} cy="86" r="1.8" fill={GOLD} opacity="0.3" />
          </g>
        ))}
      </svg>
    </div>
  );
}

/* ────────────────────────────────────────────────────────── */
/* 05 — CTA CRYSTAL NETWORK                                   */
/* ────────────────────────────────────────────────────────── */
export function CTACrystalNetwork({ size = 320 }) {
  return (
    <div className="relative" style={{ width: size, height: size }} aria-hidden>
      {/* Sapphire-amber connected network behind the crystal */}
      <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid meet">
        <defs>
          <radialGradient id="cta-halo" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={SAPPHIRE_CORE} stopOpacity="0.65" />
            <stop offset="100%" stopColor={SAPPHIRE} stopOpacity="0" />
          </radialGradient>
          <radialGradient id="cta-node-gold" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={GOLD_BRIGHT} stopOpacity="1" />
            <stop offset="60%" stopColor={GOLD} stopOpacity="0.55" />
            <stop offset="100%" stopColor={GOLD} stopOpacity="0" />
          </radialGradient>
          <radialGradient id="cta-node-blue" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={SAPPHIRE_CORE} stopOpacity="1" />
            <stop offset="60%" stopColor={SAPPHIRE} stopOpacity="0.45" />
            <stop offset="100%" stopColor={SAPPHIRE} stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Halo behind crystal */}
        <circle cx="50" cy="46" r="38" fill="url(#cta-halo)" opacity="0.8" />

        {/* Connection network */}
        {(() => {
          const goldNodes = [
            [12, 84], [22, 90], [38, 86], [62, 86], [78, 90], [88, 84],
            [10, 66], [28, 72], [72, 72], [90, 66],
            [16, 50], [84, 50], [22, 36], [78, 36],
          ];
          const blueNodes = [
            [50, 92], [30, 80], [70, 80], [20, 62], [80, 62], [40, 48], [60, 48],
          ];
          const all = [...goldNodes, ...blueNodes];
          const lines = [];
          all.forEach((a, i) =>
            all.slice(i + 1).forEach((b, j) => {
              const dx = a[0] - b[0]; const dy = a[1] - b[1];
              const d = Math.sqrt(dx * dx + dy * dy);
              if (d < 26) {
                lines.push(
                  <line key={`l-${i}-${j}`} x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]}
                    stroke={SAPPHIRE} strokeWidth="0.12" opacity={(1 - d / 26) * 0.55} />
                );
              }
            })
          );
          return (
            <>
              {lines}
              {goldNodes.map((p, i) => (
                <g key={`g-${i}`}>
                  <circle cx={p[0]} cy={p[1]} r="2" fill="url(#cta-node-gold)" opacity="0.9" />
                  <circle cx={p[0]} cy={p[1]} r="0.55" fill={GOLD_BRIGHT}
                    className="pulse-glow" style={{ animationDelay: `${(i % 5) * 0.4}s` }} />
                </g>
              ))}
              {blueNodes.map((p, i) => (
                <g key={`b-${i}`}>
                  <circle cx={p[0]} cy={p[1]} r="1.6" fill="url(#cta-node-blue)" opacity="0.85" />
                  <circle cx={p[0]} cy={p[1]} r="0.45" fill="#fff"
                    className="pulse-glow" style={{ animationDelay: `${(i % 4) * 0.5 + 0.3}s` }} />
                </g>
              ))}
            </>
          );
        })()}

        {/* Platform rings under crystal */}
        <ellipse cx="50" cy="86" rx="22" ry="2.8" fill="none" stroke={SAPPHIRE} strokeWidth="0.25" opacity="0.55" />
        <ellipse cx="50" cy="87" rx="16" ry="1.8" fill="none" stroke={SAPPHIRE_CORE} strokeWidth="0.3" opacity="0.65" />
        <ellipse cx="50" cy="88" rx="10" ry="1.2" fill={SAPPHIRE_CORE} opacity="0.55" />
        <ellipse cx="50" cy="88" rx="5" ry="0.6" fill="#fff" opacity="0.75" />
      </svg>

      {/* Crystal image floating above the network */}
      <img
        src={IMAGES.ctaCrystal}
        alt=""
        className="absolute left-1/2 -translate-x-1/2 top-[4%] h-[78%] object-contain ambient-pulse"
        style={{
          mixBlendMode: "screen",
          filter: "drop-shadow(0 0 18px rgba(96,165,250,0.55))",
        }}
        draggable="false"
      />
    </div>
  );
}
